import React from 'react';
import { Box, Link, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, Outlet } from 'react-router-dom';
import ErrorBoundary from 'components/feedback/ErrorBoundary';
import TenantHead from 'components/seo/TenantHead';
import { ROUTES } from 'config/routes';
import { useAuthPageCopy } from 'hooks/useAuthPageCopy';

const AuthLayout: React.FC = () => {
  const copy = useAuthPageCopy();
  const {
    siteName,
    logoSrc,
    panelBackgroundSrc,
    showBrandPanel,
    showLogo,
    showBackToStore,
    showCopyright,
    panelTitle,
    panelSubtitle,
    backToStoreLabel,
    copyrightText,
  } = copy;

  const logo = logoSrc ? (
    <Box
      component="img"
      src={logoSrc}
      alt={siteName}
      sx={{ maxHeight: { xs: 40, md: 72 }, maxWidth: { xs: 160, md: 280 }, width: 'auto' }}
    />
  ) : showLogo ? (
    <Typography variant="h5" fontWeight={800} letterSpacing={-0.3}>
      {siteName}
    </Typography>
  ) : null;

  const logoLink = logo ? (
    <Link
      component={RouterLink}
      to={ROUTES.HOME}
      underline="none"
      color="inherit"
      aria-label={siteName}
      sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', width: 'fit-content' }}
    >
      {logoSrc ? (
        <Box
          sx={{
            bgcolor: 'common.white',
            borderRadius: 2,
            px: 2,
            py: 1.5,
            display: 'inline-flex',
            alignItems: 'center',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          }}
        >
          {logo}
        </Box>
      ) : (
        logo
      )}
    </Link>
  ) : null;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        bgcolor: 'background.default',
      }}
    >
      <TenantHead />

      {showBrandPanel && (
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'space-between',
            width: { md: '42%', lg: '44%' },
            p: { md: 5, lg: 7 },
            color: 'primary.contrastText',
            bgcolor: 'primary.main',
            backgroundImage: (muiTheme) => {
              const overlay = `linear-gradient(165deg, ${alpha(muiTheme.palette.primary.main, panelBackgroundSrc ? 0.78 : 1)} 0%, ${alpha(muiTheme.palette.primary.dark, panelBackgroundSrc ? 0.88 : 1)} 78%)`;
              return panelBackgroundSrc ? `${overlay}, url(${JSON.stringify(panelBackgroundSrc)})` : overlay;
            },
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {!panelBackgroundSrc && (
            <>
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  width: 360,
                  height: 360,
                  borderRadius: '50%',
                  bgcolor: (muiTheme) => alpha(muiTheme.palette.common.white, 0.08),
                  top: -90,
                  right: -80,
                }}
              />
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  width: 220,
                  height: 220,
                  borderRadius: '50%',
                  bgcolor: (muiTheme) => alpha(muiTheme.palette.common.white, 0.06),
                  bottom: 80,
                  left: -70,
                }}
              />
            </>
          )}

          {logoLink ?? <Box />}

          <Stack spacing={1.5} sx={{ position: 'relative', maxWidth: 420 }}>
            {panelTitle ? (
              <Typography variant="h3" component="p" fontWeight={800} sx={{ letterSpacing: -0.6, lineHeight: 1.15 }}>
                {panelTitle}
              </Typography>
            ) : null}
            {panelSubtitle ? (
              <Typography variant="body1" sx={{ opacity: 0.92, fontSize: '1.05rem' }}>
                {panelSubtitle}
              </Typography>
            ) : null}
          </Stack>

          {showCopyright && copyrightText ? (
            <Typography variant="caption" sx={{ position: 'relative', opacity: 0.75 }}>
              {copyrightText}
            </Typography>
          ) : (
            <Box />
          )}
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          bgcolor: (muiTheme) => (muiTheme.palette.mode === 'dark' ? 'background.default' : 'grey.50'),
        }}
      >
        {showLogo && (
          <Box
            sx={{
              display: { xs: 'flex', md: showBrandPanel ? 'none' : 'flex' },
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              py: 1.75,
              px: 2,
            }}
          >
            {logoLink}
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 2, sm: 3 },
            py: { xs: 3, md: 5 },
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 440 }}>
            {showBackToStore && (
              <Link
                component={RouterLink}
                to={ROUTES.HOME}
                underline="hover"
                color="text.secondary"
                variant="body2"
                sx={{
                  display: { xs: 'none', md: 'inline-flex' },
                  alignItems: 'center',
                  gap: 0.75,
                  mb: 2.5,
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 18 }} />
                {backToStoreLabel}
              </Link>
            )}

            <Paper
              elevation={0}
              sx={{
                width: '100%',
                p: { xs: 3, sm: 4 },
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: (muiTheme) =>
                  muiTheme.palette.mode === 'dark' ? 'none' : '0 12px 40px rgba(15, 23, 42, 0.08)',
              }}
            >
              <ErrorBoundary variant="page">
                <Outlet />
              </ErrorBoundary>
            </Paper>

            {showBackToStore && (
              <Link
                component={RouterLink}
                to={ROUTES.HOME}
                underline="hover"
                color="text.secondary"
                variant="body2"
                sx={{
                  display: { xs: 'inline-flex', md: 'none' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.75,
                  mt: 3,
                  width: '100%',
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 18 }} />
                {backToStoreLabel}
              </Link>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AuthLayout;
